import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  Calendar as CalIcon,
  ShieldCheck,
  ArrowRight,
  Volume2,
  VolumeX,
  Sparkles,
  Check,
  Users,
  Heart,
  Handshake as HandshakeIcon,
  Home as HomeIcon,
  Coffee,
  Zap,
  MapPin,
  Sun,
  ChevronRight,
  ClipboardList,
  Trophy,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { VIDEOS, PALETTES } from "@/config/AssetSpine";

// ============================================================
// /founders — The Last 50 Founders Club
// ------------------------------------------------------------
// Tone: "Helping Hand" — human helping human. Business helping business.
// A neighbor-style utility page — not premium, not corporate.
// Skin: PALETTES.FOUNDERS · warm walnut + honey amber + clean windows.
//
// Modules (all Grit & Gear):
//   01 · Answer Your Phones          — Amanda 4.0 24/7 intake
//   02 · Stabilize Your Back-Office  — Command Calendar + Client Note Spine
//   03 · Shield Your Leads           — 24/7 system monitoring
//
// CTA: "Join the 100 — $89/mo Utility Rate"
// ============================================================

const P = PALETTES.FOUNDERS;
const RATE = "$89/mo";
const SEAT_COUNT = 100;

// ============================================================
// Root
// ============================================================
export default function Founders() {
  return (
    <div className="min-h-screen text-white relative" style={{ background: P.BG }}>
      <Backdrop />

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12 space-y-12">
        <Hero />
        <Mission />
        <ModulesGrid />
        <SeatMeter />
        <FoundersCTA />
      </main>
    </div>
  );
}

// ============================================================
// Backdrop — warm wood grain + clean window light
// ============================================================
function Backdrop() {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      {/* Warm wood tone base */}
      <div
        className="absolute inset-0"
        style={{
          background:
            `radial-gradient(1100px 700px at 15% -10%, ${P.PRIMARY}22, transparent 60%),` +
            `radial-gradient(900px 500px at 100% 15%, ${P.ACCENT}0F, transparent 60%),` +
            `linear-gradient(180deg, ${P.BG} 0%, ${P.BG_HI} 55%, ${P.BG} 100%)`,
        }}
      />
      {/* Faint wood grain streaks (horizontal repeating pattern) */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.05]" xmlns="http://www.w3.org/2000/svg">
        <filter id="f-wood">
          <feTurbulence type="fractalNoise" baseFrequency="0.02 0.7" numOctaves="2" seed="4" />
          <feColorMatrix values="0 0 0 0 0.9  0 0 0 0 0.7  0 0 0 0 0.4  0 0 0 1 0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#f-wood)" />
      </svg>
      {/* Clean window grid — subtle overlay */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            `linear-gradient(${P.ACCENT}55 1px, transparent 1px),` +
            `linear-gradient(90deg, ${P.ACCENT}55 1px, transparent 1px)`,
          backgroundSize: "84px 84px",
          maskImage: "radial-gradient(ellipse 1100px 700px at 50% 30%, black 40%, transparent 90%)",
          WebkitMaskImage: "radial-gradient(ellipse 1100px 700px at 50% 30%, black 40%, transparent 90%)",
        }}
      />
      <style>{`
        @keyframes f-shine { 0%{transform:translateX(-120%)} 100%{transform:translateX(220%)} }
        @keyframes f-pulse { 0%,100%{opacity:.6} 50%{opacity:1} }
        @keyframes f-glow  { 0%,100%{box-shadow:0 0 0 0 ${P.PRIMARY}00} 50%{box-shadow:0 0 30px 4px ${P.PRIMARY}55} }
      `}</style>
    </div>
  );
}

// ============================================================
// HERO — Amanda 4.0 · helping hand
// ============================================================
function Hero() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (v.paused) v.play().catch(() => {});
  };
  return (
    <section data-testid="fd-hero">
      <div
        className="text-[10px] uppercase tracking-[0.32em] font-mono flex items-center gap-2 mb-3"
        style={{ color: P.PRIMARY }}
      >
        <HandshakeIcon size={11} /> The Last 50 Founders Club · Neighbor-to-Neighbor
      </div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white mb-4 max-w-4xl">
        We&apos;ll stabilize your back-office, answer your phones,{" "}
        <span style={{ color: P.PRIMARY }} className="italic">
          and shield your leads
        </span>{" "}
        for a flat {RATE} utility rate.
      </h1>
      <p className="text-sm sm:text-base leading-relaxed max-w-3xl" style={{ color: P.ACCENT_DEEP }}>
        No contracts to memorize. No dashboards to learn. Just a utility bill
        on the first of the month and a real AI team quietly running the
        parts of your business that never stop moving.
      </p>

      <div className="relative mx-auto w-full mt-6">
        <div
          className="absolute -inset-1 rounded-sm pointer-events-none"
          style={{
            background: `linear-gradient(135deg, ${P.PRIMARY}55, ${P.ACCENT}22, ${P.PRIMARY}55)`,
            filter: "blur(18px)",
            opacity: 0.55,
          }}
        />
        <div
          className="relative rounded-sm overflow-hidden border"
          style={{
            borderColor: `${P.PRIMARY}80`,
            boxShadow: `0 30px 80px rgba(0,0,0,0.55), 0 0 60px ${P.PRIMARY}22`,
            background: "#000",
          }}
        >
          <video
            ref={videoRef}
            src={VIDEOS.AMANDA_FOUNDER}
            autoPlay
            muted
            playsInline
            controls
            data-testid="fd-hero-video-el"
            className="w-full h-auto block bg-black"
          />
          <button
            type="button"
            onClick={toggle}
            data-testid="fd-hero-mute"
            className="absolute top-3 right-3 z-10 flex items-center gap-2 px-3 py-1.5 rounded-sm backdrop-blur transition-colors"
            style={{
              background: `${P.BG}CC`,
              border: `1px solid ${P.PRIMARY}66`,
              color: P.PRIMARY,
            }}
          >
            {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
            <span className="text-[10px] uppercase tracking-[0.22em] font-mono">
              {muted ? "Tap to unmute" : "Muted off"}
            </span>
          </button>
          <div
            className="absolute top-3 left-3 z-10 flex items-center gap-2 px-3 py-1.5 rounded-sm backdrop-blur border"
            style={{ background: `${P.BG}CC`, borderColor: `${P.PRIMARY}66` }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: P.PRIMARY, boxShadow: `0 0 8px ${P.PRIMARY}` }}
            />
            <span className="text-[9px] uppercase tracking-[0.22em] font-mono" style={{ color: P.INK }}>
              Amanda · Live · Neighbor Line
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// MISSION — the ask · human tone
// ============================================================
function Mission() {
  return (
    <section
      className="rounded-sm border overflow-hidden relative"
      style={{
        borderColor: `${P.PRIMARY}55`,
        background: `linear-gradient(160deg, ${P.PRIMARY}18 0%, ${P.BG_HI}E6 55%, ${P.PRIMARY}12 100%)`,
      }}
      data-testid="fd-mission"
    >
      <div className="relative p-6 sm:p-10 grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
        {/* National storefront triptych — urban / rural / suburban */}
        <div className="lg:col-span-2">
          <NationalTriptych />
        </div>

        {/* Mission copy */}
        <div className="lg:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.36em] font-mono flex items-center gap-2 mb-3" style={{ color: P.PRIMARY }}>
            <Heart size={12} /> The Mission
          </div>
          <div
            className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-sm border"
            style={{ borderColor: `${P.PRIMARY}66`, background: `${P.PRIMARY}14`, color: P.PRIMARY }}
            data-testid="fd-national-anchor"
          >
            <MapPin size={12} />
            <span className="text-[10px] uppercase tracking-[0.28em] font-mono">
              <span style={{ color: P.INK }}>We are anchoring US-based small businesses</span> across the country.
            </span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight text-white">
            I&apos;m looking for{" "}
            <span style={{ color: P.PRIMARY }} className="italic">
              100 partners
            </span>{" "}
            to help refine the bridge between technology and real-world results.
          </h2>
          <p className="mt-4 text-sm sm:text-base leading-relaxed" style={{ color: P.INK }}>
            You give me your honest feedback — what works, what breaks, what
            your customers actually notice. I give you the utility at{" "}
            <span style={{ color: P.PRIMARY, fontWeight: 600 }}>{RATE}</span>{" "}
            and lock the Founders Rate for you for life.
          </p>
          <p className="mt-3 text-sm sm:text-base leading-relaxed" style={{ color: P.MUTED }}>
            No sales team. No enterprise contract. Business helping business.
            You&apos;ll have my cell for the first year.
          </p>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { icon: HandshakeIcon, k: "The Trade",   v: "Your feedback → your rate locked forever." },
              { icon: Users,         k: "The Room",    v: `Only ${SEAT_COUNT} seats. US-based Founders only.` },
              { icon: Zap,           k: "First Look",  v: "Founders get first look at new rollouts." },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={i}
                  className="rounded-sm border p-3"
                  style={{ borderColor: `${P.PRIMARY}44`, background: `${P.BG}A6` }}
                >
                  <div className="flex items-center gap-2 text-[9px] uppercase tracking-[0.28em] font-mono" style={{ color: P.PRIMARY }}>
                    <Icon size={12} /> {c.k}
                  </div>
                  <div className="mt-1.5 text-[12px] leading-snug" style={{ color: P.INK }}>{c.v}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// Photographic assets for the National Triptych (US small-business mix).
// Emergent-hosted CDN; loaded on demand.
const TRIPTYCH_PHOTOS = [
  {
    label: "Urban",
    caption: "City storefronts · Main Street density",
    src: "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/320a27d26d6abf387de55661dd60b08c430e16b582bf6957d7a5f05ba8a48b89.jpeg",
  },
  {
    label: "Rural",
    caption: "Country shops · County-road anchors",
    src: "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/76356290b44d3606d947ae4da42e823ee8cba4c73541f94cc2419c36d6414a5e.jpeg",
  },
  {
    label: "Suburban",
    caption: "Neighborhood offices · Shopping-plaza pros",
    src: "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/e9ff4b801a9ca912feeac3af9ee119361b50711e978dfd062e5326e692cb1b6a.jpeg",
  },
];

function NationalTriptych() {
  // Three real photos representing the national US small-business mix.
  // Urban / Rural / Suburban. Warm honey overlay unifies with the
  // Grit & Gear palette so the photos read as one editorial block
  // rather than three mismatched stock shots.
  return (
    <div
      className="relative w-full rounded-sm border overflow-hidden"
      style={{
        aspectRatio: "4 / 6",
        borderColor: `${P.PRIMARY}66`,
        background: `linear-gradient(180deg, ${P.BG_HI} 0%, ${P.BG} 100%)`,
        boxShadow: `0 30px 70px -20px rgba(0,0,0,0.7)`,
      }}
      data-testid="fd-national-triptych"
    >
      <div className="absolute inset-2 grid grid-rows-3 gap-2">
        {TRIPTYCH_PHOTOS.map((p, i) => (
          <TriptychPanel key={p.label} photo={p} index={i} />
        ))}
      </div>

      {/* Bottom banner label */}
      <div
        className="absolute inset-x-0 bottom-0 py-2 px-3 flex items-center justify-between text-[9px] uppercase tracking-[0.28em] font-mono border-t z-10"
        style={{ background: `${P.BG}D9`, borderColor: `${P.PRIMARY}44`, color: P.MUTED }}
      >
        <span style={{ color: P.PRIMARY }}>National Mix · Every Zip</span>
        <span>Coast to Coast · US-Based</span>
      </div>

      {/* Sun-flare accent */}
      <div
        className="absolute -top-6 -right-6 w-32 h-32 rounded-full pointer-events-none z-0"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${P.PRIMARY}55 0%, transparent 70%)`,
          animation: "f-pulse 4s ease-in-out infinite",
        }}
      />
    </div>
  );
}

function TriptychPanel({ photo, index }) {
  return (
    <div
      className="relative rounded-sm overflow-hidden border"
      style={{
        borderColor: `${P.PRIMARY}44`,
        background: P.BG_HI,
      }}
      data-testid={`fd-triptych-${photo.label.toLowerCase()}`}
    >
      <img
        src={photo.src}
        alt={`${photo.label} US small business · ${photo.caption}`}
        loading={index === 0 ? "eager" : "lazy"}
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          filter: "saturate(0.9) contrast(1.05)",
        }}
      />
      {/* Warm honey gradient overlay to unify with Grit & Gear palette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            `linear-gradient(180deg, ${P.BG}00 0%, ${P.BG}55 70%, ${P.BG}CC 100%),` +
            `linear-gradient(135deg, ${P.PRIMARY}18 0%, transparent 55%)`,
        }}
      />
      {/* Grain texture overlay for the "Grit" feel on top of real photos */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.12] pointer-events-none mix-blend-overlay" xmlns="http://www.w3.org/2000/svg">
        <filter id={`fd-grit-${index}`}>
          <feTurbulence type="fractalNoise" baseFrequency="1.2" numOctaves="2" stitchTiles="stitch" seed={index + 3} />
          <feColorMatrix values="0 0 0 0 0.85  0 0 0 0 0.7  0 0 0 0 0.4  0 0 0 1 0" />
        </filter>
        <rect width="100%" height="100%" filter={`url(#fd-grit-${index})`} />
      </svg>

      {/* Label pill */}
      <div
        className="absolute top-2 left-2 px-2 py-1 rounded-sm border text-[9px] uppercase tracking-[0.28em] font-mono"
        style={{
          borderColor: `${P.PRIMARY}88`,
          background: `${P.BG}CC`,
          color: P.PRIMARY,
        }}
      >
        {photo.label}
      </div>

      {/* Caption on hover / bottom */}
      <div
        className="absolute bottom-2 left-2 right-2 text-[10px] font-mono uppercase tracking-[0.22em]"
        style={{ color: P.ACCENT }}
      >
        {photo.caption}
      </div>
    </div>
  );
}

// ============================================================
// MODULES — the 3-lane utility
// ============================================================
function ModulesGrid() {
  return (
    <section data-testid="fd-modules">
      <div className="text-[10px] uppercase tracking-[0.36em] font-mono flex items-center gap-2 mb-4" style={{ color: P.PRIMARY }}>
        <Sun size={12} /> What the Utility Does · The Three Lanes
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <AnswerPhonesModule />
        <StabilizeModule />
        <ShieldLeadsModule />
      </div>
    </section>
  );
}

function ModuleFrame({ n, title, subtitle, icon: Icon, children, testId }) {
  return (
    <div
      className="rounded-sm border overflow-hidden flex flex-col relative"
      style={{
        borderColor: `${P.PRIMARY}55`,
        background: `linear-gradient(180deg, ${P.PRIMARY}10 0%, ${P.BG_HI}F2 100%)`,
      }}
      data-testid={testId}
    >
      <div
        className="px-4 py-3 border-b flex items-center gap-3"
        style={{ borderColor: "rgba(255,255,255,0.06)", background: `${P.PRIMARY}0F` }}
      >
        <div
          className="w-9 h-9 rounded-sm border flex items-center justify-center shrink-0"
          style={{ borderColor: `${P.PRIMARY}66`, background: `${P.PRIMARY}18`, color: P.PRIMARY }}
        >
          <Icon size={17} />
        </div>
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-[0.32em] font-mono" style={{ color: P.PRIMARY }}>
            Lane {n}
          </div>
          <div className="font-display text-white text-base sm:text-lg leading-tight">{title}</div>
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col gap-3">
        <p className="text-[13px] leading-relaxed" style={{ color: P.INK }}>{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

function AnswerPhonesModule() {
  return (
    <ModuleFrame
      n="01"
      title="Answer Your Phones"
      subtitle="Amanda 4.0 picks up every call, 24/7. Books the job, files the notes, texts a confirmation before the caller hangs up."
      icon={Phone}
      testId="fd-mod-phones"
    >
      <div
        className="rounded-sm border p-3"
        style={{ borderColor: `${P.PRIMARY}44`, background: `${P.BG}A6` }}
      >
        <div className="text-[10px] uppercase tracking-[0.28em] font-mono flex items-center gap-2" style={{ color: P.ACCENT }}>
          <Sparkles size={11} style={{ color: P.PRIMARY }} /> Last Night · Live Test
        </div>
        <div className="mt-2 space-y-1.5 text-[12px]" style={{ color: P.INK }}>
          <div className="flex items-center justify-between">
            <span style={{ color: P.MUTED }}>Calls answered</span>
            <span className="font-display text-lg" style={{ color: P.INK }}>34</span>
          </div>
          <div className="flex items-center justify-between">
            <span style={{ color: P.MUTED }}>Jobs booked</span>
            <span className="font-display text-lg" style={{ color: P.PRIMARY }}>21</span>
          </div>
          <div className="flex items-center justify-between">
            <span style={{ color: P.MUTED }}>Missed calls</span>
            <span className="font-display text-lg" style={{ color: P.PRIMARY }}>0</span>
          </div>
        </div>
      </div>
      <div className="text-[11px] uppercase tracking-[0.22em] font-mono flex items-center gap-1.5 mt-auto" style={{ color: P.PRIMARY }}>
        <Check size={11} /> Zero voicemail · zero missed
      </div>
    </ModuleFrame>
  );
}

function StabilizeModule() {
  return (
    <ModuleFrame
      n="02"
      title="Stabilize Your Back-Office"
      subtitle="Command Calendar and the Client Note Spine keep your crew, your customers, and your schedule locked — voice-to-text from the field."
      icon={CalIcon}
      testId="fd-mod-stabilize"
    >
      <div className="grid grid-cols-1 gap-2">
        <div
          className="rounded-sm border px-3 py-2"
          style={{ borderColor: `${P.PRIMARY}55`, background: `${P.PRIMARY}12` }}
        >
          <div className="text-[9px] uppercase tracking-[0.28em] font-mono flex items-center gap-1.5" style={{ color: P.PRIMARY }}>
            <CalIcon size={10} /> Today · Command Calendar
          </div>
          <div className="mt-1 text-[11px] leading-tight" style={{ color: P.INK }}>
            8a · Crew A · Overhead 16×7 install<br />
            10a · Crew B · Spring replacement<br />
            2p · Bid walk · Prospect St
          </div>
        </div>
        <div
          className="rounded-sm border px-3 py-2"
          style={{ borderColor: `${P.ACCENT}44`, background: `${P.ACCENT}0A` }}
        >
          <div className="text-[9px] uppercase tracking-[0.28em] font-mono flex items-center gap-1.5" style={{ color: P.ACCENT }}>
            <ClipboardList size={10} /> Client Note Spine · fresh entry
          </div>
          <div className="mt-1 text-[11px] leading-tight italic" style={{ color: P.INK }}>
            &quot;Wants belt drive not chain. Detached garage. Tips cash for careful work.&quot;
          </div>
        </div>
      </div>
      <div className="text-[11px] uppercase tracking-[0.22em] font-mono flex items-center gap-1.5 mt-auto" style={{ color: P.PRIMARY }}>
        <Check size={11} /> Nothing forgotten · nothing dropped
      </div>
    </ModuleFrame>
  );
}

function ShieldLeadsModule() {
  return (
    <ModuleFrame
      n="03"
      title="Shield Your Leads"
      subtitle="24/7 monitoring watches every inbound channel — website form, Google, Facebook, referral text. If a lead hits and nobody moves, the alarm goes off."
      icon={ShieldCheck}
      testId="fd-mod-shield"
    >
      <div
        className="rounded-sm border p-3"
        style={{ borderColor: `${P.PRIMARY}44`, background: `${P.BG}A6` }}
      >
        <div className="text-[10px] uppercase tracking-[0.28em] font-mono flex items-center gap-2 mb-2" style={{ color: P.PRIMARY }}>
          <ShieldCheck size={11} /> Monitor · Live
        </div>
        <div className="space-y-1.5">
          {[
            { k: "Website form",   v: "OK", ok: true },
            { k: "Google reviews", v: "OK", ok: true },
            { k: "Facebook DM",    v: "OK", ok: true },
            { k: "Voicemail",      v: "0",  ok: true },
          ].map((row, i) => (
            <div key={i} className="flex items-center justify-between text-[11px] font-mono" style={{ color: P.INK }}>
              <span style={{ color: P.MUTED }}>{row.k}</span>
              <span className="inline-flex items-center gap-1 uppercase tracking-[0.22em]" style={{ color: P.PRIMARY }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: P.PRIMARY, boxShadow: `0 0 6px ${P.PRIMARY}` }} />
                {row.v}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="text-[11px] uppercase tracking-[0.22em] font-mono flex items-center gap-1.5 mt-auto" style={{ color: P.PRIMARY }}>
        <Check size={11} /> Every channel · every hour
      </div>
    </ModuleFrame>
  );
}

// ============================================================
// SEAT METER — 100 partners
// ============================================================
function SeatMeter() {
  // Static demo — 27 of 100 taken
  const taken = 27;
  return (
    <section
      className="rounded-sm border overflow-hidden relative"
      style={{
        borderColor: `${P.PRIMARY}55`,
        background: `linear-gradient(180deg, ${P.PRIMARY}10 0%, ${P.BG_HI}E6 100%)`,
      }}
      data-testid="fd-seat-meter"
    >
      <div className="p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div
          className="w-11 h-11 rounded-sm border flex items-center justify-center shrink-0"
          style={{ borderColor: `${P.PRIMARY}66`, background: `${P.PRIMARY}18`, color: P.PRIMARY }}
        >
          <Users size={19} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.32em] font-mono" style={{ color: P.PRIMARY }}>
            The Room · Founders Only
          </div>
          <div className="font-display text-white text-lg sm:text-xl leading-tight mt-0.5">
            <span style={{ color: P.PRIMARY }}>{taken}</span>
            {" "}of{" "}
            <span style={{ color: P.INK }}>{SEAT_COUNT}</span>
            {" "}seats claimed · {SEAT_COUNT - taken} still open
          </div>
          {/* Progress bar of 10 pips */}
          <div className="mt-3 grid grid-cols-10 gap-1 max-w-md">
            {[...Array(10)].map((_, i) => {
              const filled = i < Math.round(taken / 10);
              return (
                <span
                  key={i}
                  className="h-2 rounded-sm"
                  style={{
                    background: filled ? P.PRIMARY : `${P.PRIMARY}20`,
                    boxShadow: filled ? `0 0 8px -1px ${P.PRIMARY}` : "none",
                  }}
                />
              );
            })}
          </div>
          <div className="mt-3 text-[11px] leading-relaxed" style={{ color: P.MUTED }}>
            When the meter hits 100, the door closes. The Founders Rate ({RATE}) never opens again.
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// CTA — join the 100
// ============================================================
function FoundersCTA() {
  return (
    <section
      className="rounded-sm border overflow-hidden relative"
      style={{
        borderColor: `${P.PRIMARY}88`,
        background: `linear-gradient(160deg, ${P.PRIMARY}22 0%, ${P.BG}D9 45%, ${P.ACCENT}0F 100%)`,
        boxShadow: `0 40px 100px -30px ${P.PRIMARY}55, 0 0 0 1px ${P.PRIMARY}22 inset`,
      }}
      data-testid="fd-cta"
    >
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${P.PRIMARY}, ${P.ACCENT}, ${P.PRIMARY}, transparent)` }}
      />
      <div
        className="absolute inset-y-0 w-40 pointer-events-none"
        style={{
          background: `linear-gradient(90deg, transparent, ${P.PRIMARY}22, transparent)`,
          animation: "f-shine 5s ease-in-out infinite",
        }}
      />

      <div className="relative p-6 sm:p-10">
        <div className="text-[10px] uppercase tracking-[0.36em] font-mono flex items-center gap-2 mb-3" style={{ color: P.PRIMARY }}>
          <Trophy size={12} /> The Founders Rate · Locked Forever
        </div>

        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-white leading-tight max-w-3xl">
          Join the 100.{" "}
          <span style={{ color: P.PRIMARY }} className="italic">
            {RATE} utility rate.
          </span>{" "}
          One rate, one line, one handshake.
        </h2>
        <p className="mt-3 text-sm sm:text-base leading-relaxed max-w-3xl" style={{ color: P.MUTED }}>
          Business helping business. You&apos;ll have my cell for the first
          year. Your rate never rises — not when we open to the public, not
          when we hit 10,000 customers, not ever.
        </p>

        <div className="mt-6 rounded-sm border p-4 grid grid-cols-1 md:grid-cols-4 gap-3" style={{ borderColor: `${P.PRIMARY}44`, background: `${P.BG_HI}A6` }}>
          {[
            { icon: Phone,       label: "Phones",  body: "24/7 answer · zero voicemail." },
            { icon: CalIcon,     label: "Back-Office", body: "Calendar + note spine, always fresh." },
            { icon: ShieldCheck, label: "Leads",   body: "Every channel monitored, hourly." },
            { icon: Sparkles,    label: "First Look", body: "Founders get first look at new rollouts." },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-sm border flex items-center justify-center shrink-0"
                  style={{ borderColor: `${P.PRIMARY}66`, background: `${P.PRIMARY}18`, color: P.PRIMARY }}
                >
                  <Icon size={14} />
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.28em] font-mono" style={{ color: P.PRIMARY }}>{f.label}</div>
                  <div className="text-[12px] leading-relaxed mt-0.5" style={{ color: P.INK }}>{f.body}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          <Link
            to="/induction?vertical=founders"
            data-testid="fd-cta-primary"
            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-sm text-sm font-display uppercase tracking-[0.24em] transition-transform active:scale-[0.99]"
            style={{
              background: `linear-gradient(90deg, ${P.PRIMARY_DEEP} 0%, ${P.PRIMARY} 100%)`,
              border: `1px solid ${P.PRIMARY}`,
              color: P.BG,
              boxShadow: `0 0 32px -6px ${P.PRIMARY}`,
            }}
          >
            <HandshakeIcon size={15} /> Join the 100 · {RATE} Utility Rate <ArrowRight size={15} />
          </Link>
          <div className="text-[10px] uppercase tracking-[0.24em] font-mono flex items-center gap-2" style={{ color: P.MUTED }}>
            <MapPin size={11} style={{ color: P.PRIMARY }} />
            Nationwide · Founders sign by handshake, not contract
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3 text-[11px] leading-relaxed" style={{ color: P.MUTED }}>
          <HomeIcon size={13} style={{ color: P.PRIMARY }} />
          <span>
            Prefer to talk first? Text <a href="tel:+14407718409" style={{ color: P.PRIMARY }}>(440) 771-8409</a> and Amanda will put you on my calendar tomorrow.
          </span>
        </div>
      </div>
    </section>
  );
}
